import {
    useAuth0
} from '@auth0/auth0-react';
import {
    subject
} from '@casl/ability';
import {
    Box,
    Divider,
    Link as MUILink,
    useMediaQuery,
    useTheme
} from '@mui/material';
import axios from 'axios';
import {
    addDays,
    format
} from 'date-fns';
import {
    type FC,
    memo,
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    AdSlot,
    DFPManager,
    DFPSlotsProvider
} from 'react-dfp';
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom';
import Slider, {
    type Settings
} from 'react-slick';
import {
    toast
} from 'react-toastify';

import Claim from '../components/Claim';
import Section from '../components/Section';
import SubscribeToast from '../components/SubscribeToast';
import Supporters from '../components/Supporters';
import type {
    Bulletin,
    Contract
} from '../types';

import Button from '@components/typed/Button';
import Typography from '@components/typed/Typography';

import Loader from '@components/shared/Loader';

import Flipbook from '@components/flipbook/View/Flipbook';

import {
    AppContext
} from '@contexts/AppContext';

import customerDataRequest from '@services/api/customer-data';

import {
    getAuthToken,
    loadListingAbilitiesForUser
} from '@auth/authHelpers';
import {
    AbilityContext
} from '@auth/Can';

import {
    getPdfUrlByPidCallback
} from '@utils/organization';
import {
    isThemeOrganization
} from '@utils/theme';

import {
    Descriptions
} from '@constants/Descriptions';
import {
    FULL_PUBLICATION_FORMAT
} from '@constants/publications';
import {
    ERROR_404,
    ERROR_500
} from '@constants/routes';
import {
    COLORS,
    POL_THEME,
    ThemeContext
} from '@constants/theme';
import {
    Titles
} from '@constants/titles';

import type {
    Organization
} from '@types';

import type {
    PathParams,
    ViewBulletinProps
} from './types';

import styles from './styles';

import THEMED_ADVERTISEMENT_SLOTS from './advertisements';

const BULLETIN_LIMIT = 9;

// We build a publication URL based on theme and path-friendly slug
const buildUrlPublication = (publication: string | null, isPol: boolean) => {
    if (!publication) {
        return null;
    }

    const root = isPol ? process.env.POL_BULLETINS_ROOT_URL : process.env.MCO_BULLETINS_ROOT_URL;

    return `${root}/${publication.replace(/-/g, '/')}`;
};

// We format the publish date adding one day for display rules
const getDisplayDate = (isoDate: string) => format(addDays(new Date(isoDate), 1), FULL_PUBLICATION_FORMAT);

// We normalize cross-domain file URLs when not on POL
const toMcoIfNeeded = (url: string | null, isPol: boolean) => {
    if (!url || isPol) {
        return url;
    }

    try {
        const u = new URL(url);

        if (u.hostname.includes('parishesonline')) {
            u.hostname = u.hostname.replace('parishesonline', 'mycommunityonline');
        }

        return u.toString();
    } catch {
        return url; // leave as-is on malformed URL
    }
};

const mobile: Settings = {
    arrows: false,
    dots: false,
    infinite: false,
    slidesToScroll: 2,
    slidesToShow: 2.5,
    speed: 500,
    vertical: false
};

const desktop: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 5,
    slidesToShow: 5,
    speed: 500,
    vertical: false
};

const ViewBulletin: FC<ViewBulletinProps> = () => {
    const navigate = useNavigate();

    const {
        filename,
        slug
    } = useParams<PathParams>();

    const [
        searchParams
    ] = useSearchParams();

    // We access theme and responsive utilities
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const settings = isMobile ? mobile : desktop;

    // We tap app contexts
    const themeContext = useContext(ThemeContext);
    const {
        setPageDescription,
        setPageTitle
    } = useContext(AppContext);
    const contextAbility = useContext(AbilityContext);

    // We tap authentication
    const {
        getAccessTokenSilently,
        isAuthenticated,
        isLoading: isAuthenticationLoading,
        user
    } = useAuth0();

    // We compute theme guards
    const isPOL = themeContext === POL_THEME;

    // We read URL query parameters
    const pid = searchParams.get('pid');
    const pub = searchParams.get('publication');
    const selectedPublicationUrlParam = searchParams.get('selectedPublication');

    // We derive publication path from params
    const publicationParam = filename ?? pub;

    // We derive canonical publication URL for current theme
    const urlPublication = useMemo(() => buildUrlPublication(publicationParam, isPOL), [
        publicationParam,
        isPOL
    ]);

    const advertisementSlot = THEMED_ADVERTISEMENT_SLOTS[themeContext || 'POL'];

    // We prefer explicit selected URL, falling back to derived one
    const urlSelectedPublication = useMemo(() => {
        const fromParam = toMcoIfNeeded(selectedPublicationUrlParam, isPOL);

        if (fromParam) {
            return fromParam;
        }

        if (urlPublication) {
            return urlPublication;
        }

        return null;
    }, [
        selectedPublicationUrlParam,
        urlPublication,
        isPOL
    ]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        organizationDetails,
        setOrganizationDetails
    ] = useState<Organization | undefined>(undefined);

    const [
        contractsList,
        setContractsList
    ] = useState<Contract[] | undefined>(undefined);

    const [
        bulletinsList,
        setBulletinsList
    ] = useState<Bulletin[]>([]);

    const [
        viewerPublication,
        setViewerPublication
    ] = useState<Bulletin | null>(null);

    const [
        publicationURL,
        setPublicationURL
    ] = useState<string | null>(null);

    const [
        prettyPublishDate,
        setPrettyPublishDate
    ] = useState<string | null>(null);

    const [
        canAdminPage,
        setCanAdminPage
    ] = useState<boolean>(false);

    const [
        canAdminAll,
        setCanAdminAll
    ] = useState<boolean>(false);

    const [
        areAdsLoaded,
        setAreAdsLoaded
    ] = useState<boolean>(false);

    const [
        isFeaturedAdEmpty,
        setIsFeaturedAdEmpty
    ] = useState<boolean>(false);

    const isPageHydrated = useRef<boolean>(false);

    const organizationDetailsRequest = useRef<Promise<void> | null>(null);
    const contractsRequest = useRef<Promise<void> | null>(null);
    const adminRightsRequest = useRef<Promise<void> | null>(null);
    const bulletinsRequest = useRef<Promise<void> | null>(null);

    // Early redirect if nothing requested
    useEffect(() => {
        if (isAuthenticationLoading) {
            return;
        }

        if (!pid && !pub && !filename && !selectedPublicationUrlParam) {
            toast.error('No publication was requested.');

            startTransition(() => navigate(`/organization/${slug}`));
        }
    }, [
        filename,
        isAuthenticationLoading,
        navigate,
        pid,
        pub,
        selectedPublicationUrlParam,
        slug
    ]);

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Force a fresh hydration for the new organization
    useEffect(() => {
        isPageHydrated.current = false;
        setOrganizationDetails(undefined);
        setContractsList(undefined);
        setBulletinsList([]);
        setViewerPublication(null);
        setPublicationURL(null);
        setCanAdminAll(false);
        setCanAdminPage(false);
    }, [
        slug
    ]);

    // Resolve PDF by PID and redirect
    const loadPdfByPid = useCallback(async () => {
        if (!organizationDetails?.salesforce_id || !pid) {
            return;
        }

        const data = await getPdfUrlByPidCallback(
            organizationDetails.salesforce_id,
            pid,
            getAccessTokenSilently
        );

        if (data?.pdfUrl) {
            startTransition(() => navigate(data.pdfUrl));
        } else {
            startTransition(() => navigate(`/organization/${slug}`));
        }
    }, [
        organizationDetails,
        pid,
        slug,
        navigate,
        getAccessTokenSilently
    ]);

    useEffect(() => {
        loadPdfByPid();
    }, [
        loadPdfByPid
    ]);

    // Maintain pretty publish date for viewer
    useEffect(() => {
        if (!viewerPublication) {
            return;
        }

        if (viewerPublication.publishDate) {
            setPrettyPublishDate(getDisplayDate(viewerPublication.publishDate));
        } else {
            setPrettyPublishDate('Archive');
        }
    }, [
        viewerPublication
    ]);

    //  Update meta tags defensively
    useEffect(() => {
        if (!organizationDetails?.physical_title || !prettyPublishDate) {
            return;
        }

        const parts = prettyPublishDate.split(', ');

        const date = (prettyPublishDate !== 'Archive' && parts.length >= 3) ? `${parts[1]}, ${parts[2]}` : prettyPublishDate;

        if (isPOL) {
            setPageTitle(
                Titles.POL_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );

            setPageDescription(
                Descriptions.POL_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );
        } else {
            setPageDescription(
                Descriptions.MCO_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );

            setPageTitle(
                Titles.MCO_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );
        }
    }, [
        organizationDetails,
        prettyPublishDate,
        isPOL,
        setPageTitle,
        setPageDescription
    ]);

    const sortedBulletins = useMemo(() => [
        ...bulletinsList
    ].sort((a, b) => new Date(b.publishDate ?? 0).getTime() - new Date(a.publishDate ?? 0).getTime()), [
        bulletinsList
    ]);

    //  Select or validate the requested publication
    useEffect(() => {
        if (!urlSelectedPublication || !sortedBulletins.length) {
            if (!urlSelectedPublication) {
                setViewerPublication(null);
                setPublicationURL(null);
            }

            return undefined;
        }

        const canonicalize = (url: string | null) => toMcoIfNeeded(url, isPOL);
        const normalizedTarget = encodeURI(canonicalize(urlSelectedPublication)!);

        const match = sortedBulletins.find((p) => encodeURI(canonicalize(p.fileUrl)!) === normalizedTarget);

        if (match) {
            setViewerPublication(match);
            setPublicationURL(null);

            return undefined;
        }

        const ctrl = new AbortController();

        (async () => {
            try {
                const response = await fetch(
                    urlSelectedPublication,
                    {
                        method: 'HEAD',
                        signal: ctrl.signal
                    }
                );

                if (response.ok) {
                    setPublicationURL(urlSelectedPublication);
                } else {
                    toast.error('The requested publication is not available.');

                    startTransition(() => navigate(`/organization/${slug}`));
                }
            } catch (error) {
                if (!ctrl.signal.aborted) {
                    toast.error('Encountered an unknown error.');
                }
            }
        })();

        return () => {
            ctrl.abort();
        };
    }, [
        urlSelectedPublication,
        sortedBulletins,
        navigate,
        slug,
        isPOL
    ]);

    //  Reset ad refresh gate on layout or org change
    useEffect(() => {
        setAreAdsLoaded(false);
    }, [
        slug,
        isMobile
    ]);

    //  Attempt to refresh ads after slots mount
    useEffect(() => {
        if (isLoading || !organizationDetails || !contractsList || areAdsLoaded) {
            return undefined;
        }

        let attempts = 0;
        let cancelled = false;

        const tryRefresh = async () => {
            if (cancelled) {
                return;
            }

            try {
                const googletag = await DFPManager.getGoogletag();

                const slots = googletag?.pubads?.().getSlots?.() ?? [];

                if (!slots.length) {
                    if (attempts < 20) {
                        setTimeout(tryRefresh, 200);
                    }

                    attempts += 1;

                    return;
                }

                slots.forEach((s) => s.clearTargeting?.());

                DFPManager.setTargetingArguments({
                    Category: organizationDetails.tags,
                    Church_ID: contractsList.map((c) => String(c.contract_id)).filter(Boolean),
                    City: organizationDetails.physical_city,
                    Salesforce_Id: organizationDetails.salesforce_id,
                    State: organizationDetails.physical_state,
                    Zip: organizationDetails.physical_postal_code
                });

                DFPManager.refresh();

                setAreAdsLoaded(true);
            } catch (error) {}
        };

        setTimeout(tryRefresh, 0);

        return () => {
            cancelled = true;
        };
    }, [
        areAdsLoaded,
        contractsList,
        isLoading,
        isMobile,
        organizationDetails,
        setAreAdsLoaded,
        slug
    ]);

    // Check to see if we have a featured supporter advertisement loaded
    useEffect(() => {
        let cancelled = false;

        if (!areAdsLoaded) {
            return undefined;
        }

        (async () => {
            try {
                const googletag = await DFPManager.getGoogletag();

                if (!googletag?.pubads || typeof googletag.pubads !== 'function') {
                    return;
                }

                const currentSlotId = isMobile ? advertisementSlot.Publication_300x100_Mobile.slotId : advertisementSlot.Publication_XL_300x600.slotId;

                googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                    if (cancelled) {
                        return;
                    }

                    if (event.slot.getSlotElementId() === currentSlotId) {
                        setIsFeaturedAdEmpty(event.isEmpty);
                    }
                });
            } catch {}
        })();

        return () => {
            cancelled = true;
        };
    }, [
        isMobile,
        advertisementSlot,
        areAdsLoaded
    ]);

    //  Data fetchers (deduplicated with refs)
    const fetchOrganizationDetails = useCallback(async () => {
        if (!slug) {
            return undefined;
        }

        if (organizationDetails) {
            return undefined;
        }

        if (organizationDetailsRequest.current) {
            return organizationDetailsRequest.current;
        }

        organizationDetailsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Organization>(
                    'get',
                    `/organizations/slug/${slug}`
                );

                const ok = isThemeOrganization(
                    data,
                    themeContext
                );

                if (!ok) {
                    navigate(ERROR_404);

                    return;
                }

                setOrganizationDetails(data);
            } catch (error) {
                if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
                    navigate(ERROR_404);
                } else {
                    navigate(ERROR_500);
                }
            } finally {
                organizationDetailsRequest.current = null;
            }
        })();

        return organizationDetailsRequest.current;
    }, [
        navigate,
        slug,
        themeContext,
        organizationDetails
    ]);

    const fetchContracts = useCallback(async () => {
        if (!organizationDetails) {
            return undefined;
        }

        if (contractsList) {
            return undefined;
        }

        if (contractsRequest.current) {
            return contractsRequest.current;
        }

        const salesforceID = organizationDetails.salesforce_id;

        contractsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Contract[]>(
                    'get',
                    `organizations/${salesforceID}/contracts`
                );

                setContractsList(data);
            } catch {
                setContractsList([]);
            } finally {
                contractsRequest.current = null;
            }
        })();

        return contractsRequest.current;
    }, [
        organizationDetails,
        contractsList
    ]);

    const fetchAdminRights = useCallback(async () => {
        if (!organizationDetails || !isAuthenticated) {
            return undefined;
        }

        if (adminRightsRequest.current) {
            return adminRightsRequest.current;
        }

        const salesforceID = organizationDetails.salesforce_id;

        adminRightsRequest.current = (async () => {
            try {
                const token = await getAuthToken(getAccessTokenSilently);

                const userAbility = await loadListingAbilitiesForUser(
                    user,
                    token,
                    contextAbility
                );

                const fetchedCanAdminAll = userAbility.can(
                    'admin',
                    'all'
                );

                const fetchedCanAdminClaimedPage = userAbility.can(
                    'admin',
                    subject(
                        'claimed',
                        {
                            id: salesforceID
                        }
                    )
                );

                setCanAdminPage(fetchedCanAdminClaimedPage);
                setCanAdminAll(fetchedCanAdminAll);
            } catch {
                setCanAdminPage(false);
                setCanAdminAll(false);
            } finally {
                adminRightsRequest.current = null;
            }
        })();

        return adminRightsRequest.current;
    }, [
        contextAbility,
        getAccessTokenSilently,
        isAuthenticated,
        organizationDetails,
        user
    ]);

    const fetchBulletinsList = useCallback(async () => {
        if (!organizationDetails) {
            return undefined;
        }

        if (bulletinsRequest.current) {
            return bulletinsRequest.current;
        }

        bulletinsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Bulletin[]>(
                    'get',
                    `/organizations/${organizationDetails.salesforce_id}/publications?limit=${BULLETIN_LIMIT}&type=${isPOL ? 'Church' : 'Other'}`
                );

                setBulletinsList(data);
            } catch (error) {
                setBulletinsList([]);
            } finally {
                bulletinsRequest.current = null;
            }
        })();

        return bulletinsRequest.current;
    }, [
        isPOL,
        organizationDetails
    ]);

    //  Hydration
    useEffect(() => {
        if (isPageHydrated.current) {
            return undefined;
        }

        let cancelled = false;

        const hydrate = async () => {
            setIsLoading(true);

            await Promise.all([
                fetchContracts(),
                fetchAdminRights(),
                fetchBulletinsList()
            ]);

            isPageHydrated.current = true;

            if (!cancelled) {
                setIsLoading(false);
            }
        };

        if (organizationDetails) {
            hydrate();
        }

        return () => {
            cancelled = true;
        };
    }, [
        organizationDetails,
        fetchBulletinsList,
        fetchContracts,
        fetchAdminRights
    ]);

    //  Fetch organization when slug or theme changes
    useEffect(() => {
        fetchOrganizationDetails();
    }, [
        fetchOrganizationDetails
    ]);

    const previousBulletins = useMemo(() => {
        if (!organizationDetails) {
            return null;
        }

        return sortedBulletins.map(({
            fileUrl,
            publishDate,
            smallThumbnailUrl
        }) => (
            <Box key={fileUrl}>
                <Typography variant={'body2'}>
                    {format(addDays(new Date(publishDate), 1), 'EEE, MMM d')}
                </Typography>
                <MUILink
                    component={Link}
                    sx={styles.thumbnailSmall}
                    to={`/publication-page/${organizationDetails.slug}?selectedPublication=${encodeURIComponent(fileUrl)}`}
                >
                    <Box
                        alt={`Bulletin thumbnail for ${format(addDays(new Date(publishDate), 1), 'MMM d, yyyy')}`}
                        component={'img'}
                        src={smallThumbnailUrl}
                    />
                </MUILink>
            </Box>
        ));
    }, [
        sortedBulletins,
        organizationDetails
    ]);

    const divider = useMemo(() => (
        <Divider
            sx={styles.divider}
        />
    ), []);

    const mobileHeader = !!organizationDetails && (
        <Box
            display={'flex'}
            flexDirection={'column'}
        >
            <Link to={`/organization/${organizationDetails.slug}`}>
                <Typography
                    aria-label={`Back to ${organizationDetails.physical_title}`}
                    variant={'h3'}
                >
                    {`< Back to ${organizationDetails.physical_title}`}
                </Typography>
            </Link>
            {
                !isFeaturedAdEmpty && (
                    <Box
                        display={'flex'}
                        flexGrow={1}
                        justifyContent={'center'}
                        my={3}
                    >
                        <AdSlot {...advertisementSlot.Publication_300x100_Mobile} />
                    </Box>
                )
            }
            <Typography
                sx={{
                    pl: 2
                }}
                variant={'h6'}
            >
                {prettyPublishDate}
            </Typography>
            {
                !!(viewerPublication?.fileUrl || publicationURL) && (
                    <Button
                        component={MUILink}
                        href={viewerPublication?.fileUrl || publicationURL || undefined}
                        rel={'noopener'}
                        sx={{
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white,
                            mt: 3
                        }}
                        target={'_blank'}
                        variant={'contained'}
                    >
                        {'View as PDF'}
                    </Button>
                )
            }
        </Box>
    );

    const desktopHeader = !!organizationDetails && (
        <Box
            alignItems={'flex-start'}
            display={'flex'}
            flexDirection={'row'}
            gap={4}
        >
            <Box>
                <Link to={`/organization/${organizationDetails.slug}`}>
                    <Typography
                        aria-label={`Back to ${organizationDetails.physical_title}`}
                        variant={'h4'}
                    >
                        {`< Back to ${organizationDetails.physical_title}`}
                    </Typography>
                </Link>
                <Typography
                    sx={{
                        pl: 2.5
                    }}
                    variant={'h5'}
                >
                    {prettyPublishDate}
                </Typography>
            </Box>
            {
                !!(viewerPublication?.fileUrl || publicationURL) && (
                    <Button
                        component={MUILink}
                        href={viewerPublication?.fileUrl || publicationURL || undefined}
                        rel={'noopener'}
                        sx={{
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white
                        }}
                        target={'_blank'}
                        variant={'contained'}
                    >
                        {'View as PDF'}
                    </Button>
                )
            }
        </Box>
    );

    return (
        (isLoading || !organizationDetails || isAuthenticationLoading) ? (
            <Loader
                color={COLORS.secondary}
            />
        ) : (
            <DFPSlotsProvider
                dfpNetworkId={process.env.DFP_NETWORK_ID!}
                disableInitialLoad
                key={`${slug}-${organizationDetails?.salesforce_id}-${isMobile ? 'm' : 'd'}`}
                singleRequest
            >
                <Box
                    component={'main'}
                    sx={styles.wrapper}
                >
                    <SubscribeToast
                        organizationDetails={organizationDetails}
                    />
                    <Box
                        sx={styles.container}
                    >
                        <Section>
                            { isMobile ? mobileHeader : desktopHeader }
                        </Section>
                        <Box
                            display={'flex'}
                            gap={{
                                lg: 8,
                                xl: 28
                            }}
                            justifyContent={'space-between'}
                        >
                            <Box
                                flex={1}
                                mt={{
                                    md: 8,
                                    xs: 3
                                }}
                            >
                                <Flipbook pdfUrl={viewerPublication?.fileUrl || publicationURL} />
                            </Box>
                            {
                                !isMobile && (
                                    <Box
                                        alignItems={'center'}
                                        display={'flex'}
                                        flexDirection={'column'}
                                        gap={1}
                                        width={300}
                                    >
                                        {/* This will be reinstated shortly */}
                                        {/* <Typography
                                            textAlign={'center'}
                                            variant={'h6'}
                                        >
                                            <b>{'Featured Supporter'}</b>
                                            {` of ${organizationDetails.physical_title}`}
                                        </Typography>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            size={'2x'}
                                        /> */}
                                        <AdSlot {...advertisementSlot.Publication_XL_300x600} />
                                    </Box>
                                )
                            }
                        </Box>
                        {
                            !!sortedBulletins.length && (
                            <>
                                {divider}
                                <Section horizontalPadding={'deep'}>
                                    <Typography
                                        ml={{
                                            md: '-18px !important'
                                        }}
                                        variant={'subtitle1'}
                                    >
                                        {'Recent Publications'}
                                    </Typography>
                                    <Box sx={styles.carousel}>
                                        <Slider {...settings}>
                                            {previousBulletins}
                                        </Slider>
                                    </Box>
                                </Section>
                            </>
                            )
                        }
                        <Supporters
                            divider={divider}
                            organizationDetails={organizationDetails}
                        />
                        {
                            (!canAdminPage && !canAdminAll) && (
                                <>
                                    {divider}
                                    <Claim
                                        organizationDetails={organizationDetails}
                                    />
                                </>
                            )
                        }
                    </Box>
                </Box>
            </DFPSlotsProvider>
        )
    );
};

export default memo(ViewBulletin);
